import { useEffect, useState } from 'react';
import { CreditCard, ListChecks, Wallet } from 'lucide-react';
import { useManager } from '../hooks/useManager';

const money = (value: unknown) => Number(value || 0).toLocaleString();

export default function Account() {
  const { account, fetchAccount, fetchCreditTransactions } = useManager();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([fetchAccount(), fetchCreditTransactions()])
      .then(([, tx]) => setTransactions(tx))
      .catch((err) => setError(err.message || 'Failed to load account'));
  }, [fetchAccount, fetchCreditTransactions]);

  if (error) return <div className="p-6 text-red-700 bg-red-50">{error}</div>;
  if (!account) return <div className="p-6 text-gray-600">Loading account...</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Account</h2>
        <p className="text-sm text-gray-600">{account.name} | {account.login_id} | Manager</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-700"><Wallet size={18} /> Left Balance</div>
          <div className="mt-2 text-2xl font-bold text-purple-950">{money(account.credits)} credits</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-700"><CreditCard size={18} /> Total Distributed</div>
          <div className="mt-2 text-2xl font-bold text-amber-950">{money(account.distributed_total)} credits</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ListChecks size={18} /> Transfers</div>
          <div className="mt-2 text-2xl font-bold text-slate-950">{money(account.transfer_count)}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-700">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Type</th>
              <th className="p-3">User</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="p-3">{row.timestamp ? new Date(row.timestamp).toLocaleString() : '-'}</td>
                <td className="p-3">{row.type || '-'}</td>
                <td className="p-3">{row.to_user_login_id || row.to_user_email || '-'}</td>
                <td className={`p-3 text-right font-semibold ${Number(row.amount) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{money(row.amount)}</td>
                <td className="p-3 text-gray-600">{row.notes || '-'}</td>
              </tr>
            ))}
            {!transactions.length && (
              <tr><td className="p-6 text-center text-gray-500" colSpan={5}>No credit transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
